/***************************************************************
 * assignInspections.js
 *
 * We will export a function `assignInspectionsToInspectors` that
 * accepts:
 *   - selectedInspectorsDetails (array)
 *   - unscheduledInspections (array)
 *
 * Returns:
 *   - An array of objects, each shaped like:
 *     {
 *       assignedInspector: { ... all inspector fields ... },
 *       assignedInspections: [ array of matched inspections ],
 *     }
 *
 * Matching Criteria:
 *   - The inspector must have `districtCodes` that contains
 *     inspection.District.
 *   - Each inspector's total assigned inspection duration must be
 *     less than 27,000 seconds (7.5 hours), leaving 30 min for lunch
 *     and 1 hour for travel time.
 *   - We assign them in order from highest to lowest priority
 *     (the unscheduledInspections list is already in descending
 *     priority order).
 ***************************************************************/

// get district neighbors object
import { districtNeighbors } from './districtNeighbors.js';

// Maximum total duration for an inspector's assignments (7.5 hours)
const MAX_DAILY_DURATION = 27000; // seconds

// Reduced duration when prescheduled inspections are already assigned (7 hours)
const MAX_DAILY_DURATION_WITH_PRESCHEDULED = 25200; // seconds


export function assignInspectionsToInspectors(selectedDate, selectedInspectorsDetails, unscheduledInspections, prescheduledInspections) {
    // 1) Build a data structure to store assigned inspections for each inspector
    const inspectorAssignments = selectedInspectorsDetails.map((inspector) => ({
      assignedInspector: { ...inspector },
      assignedInspections: [],
      totalDuration: 0
    }));

    // Keep track of which inspections have been assigned
    const assignedInspectionIds = new Set();

    // Assign prescheduled inspections
    const { inspectorAssignments: updatedAssignments, assignedInspectionIds: updatedIds } = 
      assignPrescheduledInspections(selectedDate, selectedInspectorsDetails, prescheduledInspections, inspectorAssignments, assignedInspectionIds);
    // Update our variables with the returned values
    Object.assign(inspectorAssignments, updatedAssignments);
    updatedIds.forEach(id => assignedInspectionIds.add(id));

    
    // 2) Iterate over inspections in sorted order (already sorted from highest to lowest)
    let count = 0;
    for (const inspection of unscheduledInspections) {
      count++;
      // Skip if this inspection has already been assigned
      if (assignedInspectionIds.has(inspection.INSPECTION_ID)) {
        continue;
      }

      // find an inspector whose districtCodes includes inspection.District
      // AND who has capacity for this inspection's duration
      const foundInspector = inspectorAssignments.find((entry) => {
        const codes = Array.isArray(entry.assignedInspector.districtCodes)
          ? entry.assignedInspector.districtCodes
          : [];
        const canHandle = codes.includes(inspection.District);
  
        // Check if inspector already has prescheduled inspections
        const hasPrescheduledInspections = entry.assignedInspections.some(insp => insp.Status === "Scheduled");
        
        // Use reduced duration limit if prescheduled inspections are already assigned
        const maxDuration = hasPrescheduledInspections ? MAX_DAILY_DURATION_WITH_PRESCHEDULED : MAX_DAILY_DURATION;
        
        // check duration capacity instead of count
        const hasCapacity = (entry.totalDuration + inspection.importedDuration) < maxDuration;
  
        return canHandle && hasCapacity;
      });
  
      // if found, assign this inspection and any others at the same address
      if (foundInspector) {
        // First, assign the current inspection
        foundInspector.assignedInspections.push(inspection);
        foundInspector.totalDuration += inspection.importedDuration;
        assignedInspectionIds.add(inspection.INSPECTION_ID);

        // Then, look for other inspections at the same address
        const sameAddressInspections = unscheduledInspections.filter(otherInspection => 
          !assignedInspectionIds.has(otherInspection.INSPECTION_ID) && // Not already assigned
          otherInspection.INSPECTION_ID !== inspection.INSPECTION_ID && // Not the same inspection
          otherInspection.Street === inspection.Street && // Same street address
          (foundInspector.totalDuration + otherInspection.importedDuration) < (foundInspector.assignedInspections.some(insp => insp.Status === "Scheduled") ? MAX_DAILY_DURATION_WITH_PRESCHEDULED : MAX_DAILY_DURATION) // Has duration capacity
        );

        // Assign all inspections at the same address to this inspector
        for (const sameAddressInspection of sameAddressInspections) {
          foundInspector.assignedInspections.push(sameAddressInspection);
          foundInspector.totalDuration += sameAddressInspection.importedDuration;
          assignedInspectionIds.add(sameAddressInspection.INSPECTION_ID);
        }
      }
    }

    // 3) Assign underutilized inspectors
    let finalInspectorAssignments = inspectorAssignments;
    try {
      finalInspectorAssignments = assignUnderutilizedInspectors(inspectorAssignments, unscheduledInspections, assignedInspectionIds);
    } catch (error) {
      console.error('Error assigning underutilized inspectors', error);
    }


    console.log(`Processed ${count} inspections`);
  
    // 4) Return the final array
    return finalInspectorAssignments;
  }

// Helper function to assign prescheduled inspections
function assignPrescheduledInspections(selectedDate, selectedInspectorsDetails, prescheduledInspections, inspectorAssignments, assignedInspectionIds) {
  
  const MAX_MANUAL_DAILY_DURATION = 30600; // seconds (8.5 hrs full day)

  // Helper function to check if inspection is for selected date
  function isInspectionForSelectedDate(inspection) {
    if (!inspection.AppointmentStart) return false;
    
    // Parse the appointment date (format: "2025-04-16 15:00:00")
    const [appointmentDate] = inspection.AppointmentStart.split(' ');
    const [year, month, day] = appointmentDate.split('-').map(Number);
    
    // Compare with selected date components
    if (year === selectedDate.getFullYear() &&
        month === selectedDate.getMonth() + 1 && // JavaScript months are 0-based
        day === selectedDate.getDate()) {
          
      return true;
    }

    return false;
  }

  // Helper function to check if inspector matches required engineer
  function isRequiredEngineer(inspector, inspection) {
    // If no required engineer is specified, any inspector can handle it
    if (!inspection.RequiredEngineers) return true;
    
    // Check if the inspector's ID matches the required engineer
    return inspector.INSPECTOR_ID === inspection.RequiredEngineers;
  }

  // Helper function to check for time overlap
  function hasTimeOverlap(inspection, existingInspections) {
    if (!inspection.AppointmentStart || !inspection.AppointmentFinish) return false;

    const newStart = new Date(inspection.AppointmentStart);
    const newEnd = new Date(inspection.AppointmentFinish);

    return existingInspections.some(existing => {
      if (!existing.AppointmentStart || !existing.AppointmentFinish) return false;
      
      const existingStart = new Date(existing.AppointmentStart);
      const existingEnd = new Date(existing.AppointmentFinish);

      return (newStart < existingEnd && newEnd > existingStart);
    });
  }

  function hasBackToBackConflict(inspection, existingInspections) {
    if (!inspection.AppointmentStart || !inspection.AppointmentFinish) return false;

    const newStart = new Date(inspection.AppointmentStart);
    const newEnd = new Date(inspection.AppointmentFinish);

    return existingInspections.some(existing => {
      if (!existing.AppointmentStart || !existing.AppointmentFinish) return false;
      
      const existingStart = new Date(existing.AppointmentStart);
      const existingEnd = new Date(existing.AppointmentFinish);

      // Check if new inspection starts exactly when existing one ends
      const timeAfterExisting = (newStart - existingEnd) / 1000 / 60; // minutes
      // Check if existing inspection starts exactly when new one ends  
      const timeAfterNew = (existingStart - newEnd) / 1000 / 60; // minutes

             // If either inspection starts immediately after the other ends
       if (timeAfterExisting === 0 || timeAfterNew === 0) {
         console.log(`Found back-to-back inspections:`, {
           type: timeAfterExisting === 0 ? 'new after existing' : 'existing after new',
           newInspection: {
             id: inspection.INSPECTION_ID,
             start: inspection.AppointmentStart,
             end: inspection.AppointmentFinish
           },
           existingInspection: {
             id: existing.INSPECTION_ID, 
             start: existing.AppointmentStart,
             end: existing.AppointmentFinish
           }
         });

         // TODO: Implement distance check here
         // const travelTime = estimateTravelTimeBetween(existing, inspection);
         // return travelTime > 10; // If travel time > 10 minutes, it's a conflict
         return true; // Placeholder - will always return true until distance check is implemented
       }
      
      return false;
    });
  }

  // Process each prescheduled inspection
  for (const inspection of prescheduledInspections) {
    // Skip if not for selected date
    if (!isInspectionForSelectedDate(inspection)) {
      continue;
    }

    // Skip if already assigned
    if (assignedInspectionIds.has(inspection.INSPECTION_ID)) {
      continue;
    }

    // Calculate duration once and reuse it, using two methods for safety
    let duration = parseInt(inspection.DURATION, 10);
    if (!duration) {
      const startTime = new Date(inspection.AppointmentStart);
      const endTime = new Date(inspection.AppointmentFinish);
      duration = Math.round((endTime - startTime) / 1000); // Convert ms to seconds and round
    }
    if (!duration) {
      console.warn(`No duration found for scheduled inspection ${inspection.INSPECTION_ID}`);
      duration = 0;
    }

    // Find inspector with required engineer, and capacity (no need to check district)
    const foundInspector = inspectorAssignments.find((entry) => {
      // First check if inspector has required certification
      const isRequired = isRequiredEngineer(entry.assignedInspector, inspection);
      if (!isRequired) {
        return false;
      }

      // Then check capacity using pre-calculated duration
      const hasCapacity = (entry.totalDuration + duration) <= MAX_MANUAL_DAILY_DURATION;
      if (!hasCapacity) {
        return false;
      }

      // Check appointment start time
      const appointmentStart = new Date(inspection.AppointmentStart);
      const isBeforeHours = appointmentStart.getHours() < 8;
      if (isBeforeHours) {
        console.log(`Cannot assign prescheduled inspection ${inspection.INSPECTION_ID} - starts before 8:00:`, {
          newInspection: {
            start: inspection.AppointmentStart,
            end: inspection.AppointmentFinish,
            inspector: entry.assignedInspector.INSPECTOR_ID,
            rank: inspection.rank
          }});
        return false;
      }

      // Check appointment end time
      const appointmentFinish = new Date(inspection.AppointmentFinish);
      const isAfterHours = appointmentFinish.getHours() > 16 || 
                          (appointmentFinish.getHours() === 16 && appointmentFinish.getMinutes() > 30);
      if (isAfterHours) {
        console.log(`Cannot assign prescheduled inspection ${inspection.INSPECTION_ID} - ends after 16:30:`, {
          newInspection: {
            start: inspection.AppointmentStart,
            end: inspection.AppointmentFinish,
            inspector: entry.assignedInspector.INSPECTOR_ID,
            rank: inspection.rank
          }});
        return false;
      }

      // Check for time overlap with existing assignments
      const hasOverlap = hasTimeOverlap(inspection, entry.assignedInspections);
      if (hasOverlap) {
        console.log(`Cannot assign prescheduled inspection ${inspection.INSPECTION_ID} due to overlap:`, {
          newInspection: {
            start: inspection.AppointmentStart,
            end: inspection.AppointmentFinish,
            inspector: entry.assignedInspector.INSPECTOR_ID,
            rank: inspection.rank
          }});
        return false;
      }

      // Finally check for back-to-back conflicts
      const hasBackToBack = hasBackToBackConflict(inspection, entry.assignedInspections);
      if (hasBackToBack) {
        console.log(`Cannot assign prescheduled inspection ${inspection.INSPECTION_ID} due to back-to-back conflict:`, {
          newInspection: {
            start: inspection.AppointmentStart,
            end: inspection.AppointmentFinish,
            inspector: entry.assignedInspector.INSPECTOR_ID,
            rank: inspection.rank
          }});
        return false;
      }
      
      return true;
    });

    if (foundInspector) {
      // Assign the inspection
      foundInspector.assignedInspections.push(inspection);
      foundInspector.totalDuration += duration; // Use the pre-calculated duration
      assignedInspectionIds.add(inspection.INSPECTION_ID);

      // Log assignment details
      console.log(`Assigned prescheduled inspection ${inspection.INSPECTION_ID}:`, {
        start: inspection.AppointmentStart,
        end: inspection.AppointmentFinish,
        inspector: foundInspector.assignedInspector.INSPECTOR_ID,
        rank: inspection.rank
      });
    }
  }

  console.log(`Assigned ${assignedInspectionIds.size} prescheduled inspections for ${selectedDate.toDateString()}`);

  return {
    inspectorAssignments,
    assignedInspectionIds
  };
}


function assignUnderutilizedInspectors(inspectorAssignments, unscheduledInspections, assignedInspectionIds) {
  const UNDERUTILIZED_THRESHOLD = 21600; // 6 hours in seconds
  const MAX_SECONDARY_DAILY_DURATION = MAX_DAILY_DURATION - 1800; // 30 min less than than primary to account for extended travel time
  
  // Find underutilized inspectors (less than 6 hours of work)
  const underutilizedInspectors = inspectorAssignments.filter(
    assignment => assignment.totalDuration < UNDERUTILIZED_THRESHOLD
  );

  console.log(`Found ${underutilizedInspectors.length} underutilized inspectors`);

  // Get remaining unassigned inspections (still in priority order)
  const remainingInspections = unscheduledInspections.filter(
    inspection => !assignedInspectionIds.has(inspection.INSPECTION_ID)
  );

  // Process each underutilized inspector
  for (const inspectorAssignment of underutilizedInspectors) {
    const inspector = inspectorAssignment.assignedInspector;
    
    // Get the inspector's original district codes
    const originalDistrictCodes = Array.isArray(inspector.districtCodes) 
      ? inspector.districtCodes 
      : [];
    
    // Expand district codes to include neighboring districts
    const expandedDistrictCodes = new Set(originalDistrictCodes);
    
    // Add all neighboring districts for each original district
    for (const districtCode of originalDistrictCodes) {
      const neighbors = districtNeighbors[districtCode] || [];
      neighbors.forEach(neighbor => expandedDistrictCodes.add(neighbor));
    }
    
    // Convert back to array
    const expandedDistrictCodesArray = Array.from(expandedDistrictCodes);
    
    console.log(`Inspector ${inspector.INSPECTOR_ID}: Original districts: [${originalDistrictCodes.join(', ')}], Expanded to: [${expandedDistrictCodesArray.join(', ')}]`);

    // Try to assign remaining inspections to this inspector
    for (const inspection of remainingInspections) {
      // Skip if already assigned
      if (assignedInspectionIds.has(inspection.INSPECTION_ID)) {
        continue;
      }

      // Check if inspector can handle this district (using expanded codes)
      const canHandleDistrict = expandedDistrictCodesArray.includes(inspection.District);
      
      // Check if inspector has capacity
      const hasCapacity = (inspectorAssignment.totalDuration + inspection.importedDuration) <= MAX_DAILY_DURATION;
      
      if (canHandleDistrict && hasCapacity) {
        // Assign the inspection
        inspectorAssignment.assignedInspections.push(inspection);
        inspectorAssignment.totalDuration += inspection.importedDuration;
        assignedInspectionIds.add(inspection.INSPECTION_ID);
        
        console.log(`Assigned cross-district inspection ${inspection.INSPECTION_ID} (District: ${inspection.District}) to inspector ${inspector.INSPECTOR_ID}`);
        
        // Also look for other inspections at the same address
        const sameAddressInspections = remainingInspections.filter(otherInspection => 
          !assignedInspectionIds.has(otherInspection.INSPECTION_ID) && // Not already assigned
          otherInspection.INSPECTION_ID !== inspection.INSPECTION_ID && // Not the same inspection
          otherInspection.Street === inspection.Street && // Same street address
          expandedDistrictCodesArray.includes(otherInspection.District) && // Can handle district
          (inspectorAssignment.totalDuration + otherInspection.importedDuration) <= MAX_DAILY_DURATION // Has capacity
        );

        // Assign all inspections at the same address
        for (const sameAddressInspection of sameAddressInspections) {
          inspectorAssignment.assignedInspections.push(sameAddressInspection);
          inspectorAssignment.totalDuration += sameAddressInspection.importedDuration;
          assignedInspectionIds.add(sameAddressInspection.INSPECTION_ID);
          
          console.log(`Assigned same-address inspection ${sameAddressInspection.INSPECTION_ID} to inspector ${inspector.INSPECTOR_ID}`);
        }
      }
    }
  }
  
  return inspectorAssignments;
}



